import React, { useEffect, useState } from "react";
import cx from "classnames";
import { Text, Accordion, isNullish, BigInfoIcon } from "@theorgchartdev/storybook";
import * as StateLib from "@/Utils/StateLib";
import { useChartDocumentContext } from "@/providers/ChartDocumentProvider";
import { GetFormattedDateString, SortCompareDates } from "@/Utils/DataLib";
import { ChangeDisplayRow } from "@/appComponents/Blocks/Composites/ChangeDisplayRow/ChangeDisplayRow";
import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import {
  GetCCChangeAccordionLabel,
  GetChangeAccordionLabel
} from "@/appComponents/Navigation/RightSidebar/Planning/Activity/Activity.utils";
import { GetDoc, GetWSMgr, ReRenderCanvas } from "@/Utils/StateLib";
import { useNavigationContext } from "@/providers/NavigationProvider";
import {
  BuildChartDataOverrideActions,
  GetDBFromOverride
} from "@/appComponents/Navigation/RightSidebar/ChangesDisplay/ChangesDisplay.utils";
import {
  AssignmentStatus,
  enumObjClassNames,
  enumObjectTypes,
  enumOverrideType,
  kEmployeeAssignmentStatusID,
  kNameID,
  kParentPositionID,
  kSupervisorID
} from "@coreapp/constants";
import { GetFlattenedChanges } from "@coreapp/utils/GetFlattenedChanges";
import { appstr } from "@coreapp/utils/appstr";
import { sanitizeBlockedValue } from "@coreapp/utils/FormatFieldValue";
import { formatArray } from "@coreapp/utils/formatArray";
import { QBoxOverlay_Wand } from "@coreapp/CoreApp/QBoxOverlay_Wand";
import { BoxChip } from "@/Utils/BoxChip";

export const handleDataOverrideNavigation = (override, navigateTop) => {
  const objType = override.SmartGetFieldValue("objType");
  if (objType === enumObjectTypes.holder) {
    const changeDelta = GetFlattenedChanges(override);
    const isOrphaned = changeDelta["Orphan"] === 1;
    const assignmentStatus = changeDelta[kEmployeeAssignmentStatusID];
    const isUnassignedOrReleased =
      assignmentStatus === AssignmentStatus.Unassigned ||
      assignmentStatus === AssignmentStatus.Released;
    if (isOrphaned || isUnassignedOrReleased) {
      const wsMgr = GetWSMgr();
      if (!wsMgr) return;
      // In direct-edit mode the live DB object is on fChanges[0].fDBObj;
      // displayObject may be null here unlike in plan-mode overrides.
      const dbObj = override.fChanges[0]?.fDBObj;
      const displayObject = override.SmartGetFieldValue("displayObject");
      let initialQuery = override.SmartGetFieldValue("RefID");
      const nameObj = dbObj ?? displayObject;
      if (nameObj) {
        const nameValue = nameObj.SmartGetFieldValue(kNameID);
        if (nameValue) initialQuery = nameValue;
      }
      wsMgr.BroadcastTopic("OpenEmployeeList", { employeeName: initialQuery });
      return;
    }
  }

  const doc = GetDoc();
  if (!doc) return;
  const canvas = doc.GetCanvasRef();
  if (!canvas) return;
  const dbRef = override?.fChanges[0]?.fDBObj;
  const scm = doc.GetSubChartMgr();
  const scId = scm?.FindSubChartID(dbRef, true) ?? null;
  let refId = null;
  let navTarget = dbRef;
  if (navigateTop || scId == null) {
    const boxClass = doc.GetBoxClass();
    const recIdFieldDef = boxClass?.GetRecordIDFieldDef();
    navTarget = boxClass?.FindObject(
      recIdFieldDef,
      dbRef?.SmartGetFieldValue("SupervisorID"),
      false
    );
  } else {
    refId = override.SmartGetFieldValue("RefID");
  }
  canvas.NavToBox(navTarget, false, refId, true, true);
  ReRenderCanvas();
};

const defaultOptions = { options: [] };
export const ChangesDisplay = ({
  showChanges = true,
  showFormatting = true,
  showPagination = true
}) => {
  let { chartDocument } = useChartDocumentContext();
  const defaultModeState = {
    changes: showChanges,
    formatting: showFormatting,
    pagination: showPagination
  };
  const { LaunchUIElement } = useNavigationContext();
  const [displayModes, setDisplayModes] = useState(defaultModeState);
  const [dataOptions, setDataOptions] = useState(defaultOptions);
  const [formattingOptions, setFormattingOptions] = useState(defaultOptions);
  const [paginationOptions, setPaginationOptions] = useState(defaultOptions);

  const wsMgr = GetWSMgr();
  const doc = GetDoc();

  const handleProfile = override => {
    let dbObj = GetDBFromOverride(override);
    LaunchUIElement("profile", true, { target: dbObj, ignoreSelection: true });
  };

  const handleRecordInfo = override => {
    const wsMgr = GetWSMgr();
    const dbObj = GetDBFromOverride(override);
    wsMgr.ShowRecordInfo(null, dbObj);
  };

  const handleDeleteDataOverride = override => {
    const doc = GetDoc();
    const wsMgr = GetWSMgr();
    const overrideClass = doc.GetOverrideClass();
    wsMgr.StartCheckPoint();
    overrideClass.RemoveDataOverride(GetDBFromOverride(override), override);
    wsMgr.EndCheckPoint();
    // set doc dirty
    wsMgr.SetDirty();
    // nav to changed box
    if (!isNullish(override))
      handleDataOverrideNavigation(
        override,
        override.SmartGetFieldValue("Type") === enumOverrideType.add
      );
    wsMgr.ReloadCurrentSubChart(false);
    ReRenderCanvas();
  };

  const dataHandlers = {
    handleDeleteOverride: handleDeleteDataOverride,
    handleProfile,
    handleRecordInfo,
    handleOverrideNavigation: handleDataOverrideNavigation
  };

  const handleDeleteFormatting = override => {
    const layoutMgr = doc.GetLayoutMgr();
    const plm = doc.GetPageLayoutMgr();
    const dbObj = GetDBFromOverride(override, override.objID);
    const theNode = plm.FindNodeFromDataObjID(dbObj.GetObjectID());
    const theLayout = plm.FindLayoutFromDataObj(dbObj);
    const chartBox = theNode?.GetChartBox();
    // nav to box
    wsMgr.NavToBox(dbObj);
    wsMgr.StartCheckPoint();
    // clear layout overrides
    theLayout.ClearOverrides();
    // update layout mgr
    layoutMgr.UpdateOverrideInfo(theLayout);
    // remove from canvas drawn chart box
    chartBox?.RemoveOverlay(QBoxOverlay_Wand);
    wsMgr.EndCheckPoint();
    // dirty chart
    wsMgr.SetDirty();
    ReRenderCanvas();
  };

  const handleFormattingNavigate = (override, objDB) => {
    const canvas = doc.GetCanvasRef();
    canvas.NavToBox(objDB, false, override.objID, true, true);
    ReRenderCanvas();
  };

  const formatHandlers = {
    handleDeleteOverride: handleDeleteFormatting,
    handleOverrideNavigation: handleFormattingNavigate,
    handleProfile,
    handleRecordInfo
  };

  useEffect(() => {
    BuildOptions();
  }, [chartDocument]);

  const BuildOptions = () => {
    if (displayModes.changes) {
      let normalizedDataChanges = { options: BuildDataChangeList(dataHandlers) };
      setDataOptions(normalizedDataChanges);
    }

    if (displayModes.formatting) {
      let normalizedFormattingChanges = { options: BuildFormattingChangeList(formatHandlers) };
      setFormattingOptions(normalizedFormattingChanges);
    }

    if (displayModes.pagination) {
      let normalizedPaginationChanges = { options: BuildBreaksChangeList() };
      setPaginationOptions(normalizedPaginationChanges);
    }
  };

  const containerClasses = "sidebar-content-container ocn-custom-chart-changes-container";
  const noChanges =
    dataOptions.options.length < 1 &&
    formattingOptions.options.length < 1 &&
    paginationOptions.options.length < 1;

  return (
    <React.Fragment>
      {noChanges && (
        <BigInfoIcon
          name="clipboard-check"
          color="brand"
          message={appstr("No changes have been made yet.")}
          style={{
            flex: 0,
            padding: "16px 0"
          }}
        />
      )}
      {displayModes.changes && (
        <div className={containerClasses}>
          <ChangeAccordionGroup
            accordionName={GetAccordionLabel("Data changes", dataOptions.options)}
            subLayerOptions={dataOptions.options}
            otherProps={{ dataTestId: "chart-changes-data" }}
          />
        </div>
      )}
      {displayModes.formatting && (
        <div className={containerClasses}>
          <ChangeAccordionGroup
            accordionName={GetAccordionLabel("Formatting changes", formattingOptions.options)}
            subLayerOptions={formattingOptions.options}
            otherProps={{ dataTestId: "chart-changes-formatting" }}
          />
        </div>
      )}
      {displayModes.pagination && (
        <div className={containerClasses}>
          <ChangeAccordionGroup
            accordionName={GetAccordionLabel("Pagination changes", paginationOptions.options)}
            subLayerOptions={paginationOptions.options}
            otherProps={{ dataTestId: "chart-changes-pagination" }}
          />
        </div>
      )}
    </React.Fragment>
  );
};

export const GetAccordionLabel = (prefix, options) => {
  let count = options.length;
  return appstr(prefix) + " (" + count.toString() + ")";
};

export const ChangeAccordionGroup = ({
  accordionName,
  subLayerOptions,
  removeBeforeContent = false,
  includeDivider = true,
  otherProps,
  initExpand
}) => {
  let subLayers = [];
  subLayerOptions.forEach(item => {
    item.content = <ChangeAccordionSubLayer subLayer={item} />;
    subLayers.push(item);
  });

  let beforeContent = (
    <div className={cx("sidebar-flex-column", "sidebar-flex-gap--large")}>
      {subLayers.length < 1 && (
        <div style={{ paddingLeft: "16px" }}>
          <Text size={"xsmall"} colorModifier={"lightgrey"}>
            {appstr("Changes will appear here")}
          </Text>
        </div>
      )}
    </div>
  );

  return (
    <div
      className={cx(
        "sidebar-content-item",
        includeDivider ? "sidebar-content-item--divider" : null
      )}
    >
      <Accordion
        name={accordionName}
        subLayers={subLayers}
        beforeContent={removeBeforeContent === false ? beforeContent : null}
        initExpand={initExpand}
        {...otherProps}
      />
    </div>
  );
};

export const ChangeAccordionSubLayer = ({ subLayer }) => {
  const baseClass = "sidebar-accordion-sublayer";
  const contentClass = `${baseClass}-contents`;
  const dynamicHeightClass = `${contentClass}--fixed-height`;

  return (
    <div className={baseClass}>
      <div className={cx(contentClass, dynamicHeightClass)}>
        <div className={cx("sidebar-flex-column", "sidebar-flex-gap--medium")}>
          {subLayer?.jsx}
          {subLayer.timestamp && subLayer.author && (
            <div className={cx("sidebar-flex-column", "sidebar-flex-gap--small")}>
              <Text colorModifier={"lightgrey"} size={"xsmall"}>
                {subLayer.timestamp} by {subLayer.author}{" "}
              </Text>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const BuildBreakOverrideContent = theBreak => {
  return (
    <div className={"change-panel-items-changerow"}>
      <span>
        {
          <Text size={"xsmall"} modifier={"medium"}>
            {theBreak.GetBreakTypeStr()}
          </Text>
        }
      </span>
    </div>
  );
};

const BuildFormattingOverrideContent = theOverrideDict => {
  let didBoxFormatting =
    theOverrideDict["overrides"] && Object.keys(theOverrideDict["overrides"]).length > 0;

  let cellChanges = theOverrideDict["cellOverrides"];
  let didCellFormatting = false;
  let didTextFormatting = false;

  for (let aCellChange in cellChanges) {
    let theCellChange = cellChanges[aCellChange];
    if (theCellChange["overrides"] && Object.keys(theCellChange["overrides"]).length > 0)
      didCellFormatting = true;
    if (theCellChange["textoverrides"] && Object.keys(theCellChange["textoverrides"]).length > 0)
      didTextFormatting = true;
  }

  let changeSet = [];
  if (didBoxFormatting) changeSet.push(appstr("Box Properties"));
  if (didCellFormatting) changeSet.push(appstr("Cell formatting"));
  if (didTextFormatting) changeSet.push(appstr("Text formatting"));

  let changeSummary = formatArray(changeSet) + " " + appstr("changed");
  //Change details? Does the user really need more than this?
  return (
    <div className={"change-panel-items-changerow"}>
      <span>
        {
          <Text size={"xsmall"} modifier={"medium"}>
            {changeSummary}
          </Text>
        }
      </span>
    </div>
  );
};

const BuildBreaksChangeList = clickHandler => {
  let doc = StateLib.GetDoc();
  let options = [];
  if (!doc) return options;

  let subchartMgr = doc.GetSubChartMgr();
  let overrides = subchartMgr.fManualPaginationDeltas;
  let sortedOverrides = overrides.sort((a, b) => {
    return SortCompareDates(a.fTimestamp, b.fTimestamp);
  });
  let numOverrides = sortedOverrides.length;
  for (let overrideIdx = 0; overrideIdx < numOverrides; overrideIdx++) {
    let theBreak = sortedOverrides[overrideIdx];
    const isPosition = doc.IsPositionDocument();
    const objDB = doc.FindDataObjByRecordID(
      theBreak.GetBreakParent(),
      isPosition ? enumObjClassNames.position : enumObjClassNames.box
    );
    if (!objDB) continue;
    let overrideOption = {};

    overrideOption.name = GetCCChangeAccordionLabel(
      BoxChip.FromObjDBObject(objDB).changesLabel(),
      isPosition
    );
    overrideOption.timestamp = GetFormattedDateString(new Date(theBreak.fTimestamp));
    overrideOption.author = theBreak.fUser;
    overrideOption.jsx = BuildBreakOverrideContent(theBreak);
    overrideOption.value = { override: theBreak };
    overrideOption.clickHandler = clickHandler;
    overrideOption.divider = true;
    overrideOption.dataTestId = "chart-accordion-pagination-change-" + theBreak.GetBreakParent();

    options.push(overrideOption);
  }
  return options;
};

export const BuildDataChangeList = changeHandlers => {
  let doc = StateLib.GetDoc();
  let options = [];
  if (!doc) return options;
  let overrideClass = doc.GetObjDB().GetClassByName("Override");
  let numOverrides = overrideClass.GetObjectCount();
  let overrides = overrideClass.GetObjectList();
  let sortedOverrides = overrides.sort((a, b) => {
    let change1 = a?.lastActiveChange();
    let change2 = b?.lastActiveChange();
    return SortCompareDates(change1?.fChangeDate, change2?.fChangeDate);
  });

  const boxClass = doc.GetBoxClass();
  const posClass = doc.GetPositionClass();
  const superIDField = boxClass?.GetFieldDefByID("SupervisorID");
  const superIDFieldName = superIDField?.GetFieldName();
  const personIDDef = boxClass?.GetRecordIDFieldDef(boxClass);

  for (let overrideIdx = 0; overrideIdx < numOverrides; overrideIdx++) {
    let theOverride = sortedOverrides[overrideIdx];
    const displayObj = theOverride.SmartGetFieldValue("displayObject");
    let theChange = theOverride.lastActiveChange();
    if (theChange == null) continue; // fully reverted overrides will not return any active changes

    const isPosition = theChange.fDBObj?.IsPositionObj?.();
    let currentSupervisorId = theChange.GetSupervisorID();

    const recordIDDef = theChange.fDBObj?.GetRecordIDFieldDef() ?? personIDDef;
    const objClass = theChange.fDBObj?.GetParentClass() ?? boxClass;

    let recordDB = objClass.FindObject(recordIDDef, theOverride.SmartGetFieldValue("RefID"));
    let originalSupervisorID = recordDB?.fCloneBase?.SmartGetFieldValue(
      isPosition ? kParentPositionID : kSupervisorID
    );
    if (!originalSupervisorID && displayObj) {
      originalSupervisorID = displayObj.SmartGetFieldValue(
        isPosition ? kParentPositionID : kSupervisorID
      );
    }
    if (!currentSupervisorId) {
      currentSupervisorId = originalSupervisorID;
      if (theChange.fDelta[superIDFieldName])
        currentSupervisorId = theChange.fDelta[superIDFieldName];
    }

    let activeSupervisor = objClass.FindObject(recordIDDef, currentSupervisorId);
    let origSupervisor = objClass.FindObject(recordIDDef, originalSupervisorID);
    let refID = theOverride.SmartGetFieldValue("RefID");
    let overrideOption = {};
    overrideOption.name = (
      <ActionListItem
        label={GetCCChangeAccordionLabel(GetChangesLabel(theChange), isPosition)}
        labelProps={{ size: "small", modifier: "bold" }}
        actions={BuildChartDataOverrideActions({
          override: theOverride,
          changeHandlers,
          prefix: "data-change",
          uniqueId: refID
        })}
      />
    );
    overrideOption.timestamp = GetFormattedDateString(new Date(theChange?.fChangeDate));
    overrideOption.author = theChange.fChangeAuthor;
    overrideOption.jsx = (
      <ChangeDisplayRow
        change={theChange}
        showAdditionalChanges
        currentParentDBObj={activeSupervisor}
        originalParentDBObj={origSupervisor}
        doc={doc}
        wsMgr={StateLib.GetWSMgr()}
      />
    );
    overrideOption.dataTestId = "chart-accordion-data-change-" + refID;
    overrideOption.value = { override: theOverride, change: theChange };
    overrideOption.divider = true;
    overrideOption.index = overrideIdx;
    overrideOption.isPosition = isPosition;
    options.push(overrideOption);
  }
  return options;
};

const GetChangesLabel = change => {
  const override = change?.fParentOverride;
  const dataObj = change?.fDBObj;
  const displayObj = override?.SmartGetFieldValue?.("displayObject");
  let fallbackObj = null;
  if (!dataObj && !displayObj && override) {
    try {
      fallbackObj = GetDBFromOverride(override);
    } catch {
      fallbackObj = null;
    }
  }
  const recObj = dataObj ?? displayObj ?? fallbackObj;

  if (recObj) {
    const chip = BoxChip.FromObjDBObject(recObj);

    // Overlay plan change values so the label reflects the current plan state
    // instead of stale base/CRLS-filtered values after chart navigation.
    if (override) {
      const flatChanges = GetFlattenedChanges(override);
      const parentClass = recObj.GetParentClass?.();
      if (parentClass && Object.keys(flatChanges).length > 0) {
        const titleDef =
          parentClass.GetFieldDefByID?.("Title") ?? parentClass.GetFieldDefByID?.("Position Title");
        const nameDef = parentClass.GetFieldDefByID?.("Name");
        const titleField = titleDef?.GetFieldName?.();
        const nameField = nameDef?.GetFieldName?.();
        if (titleField && flatChanges[titleField] != null) {
          chip.title = sanitizeBlockedValue(String(flatChanges[titleField]));
        }
        if (nameField && flatChanges[nameField] != null) {
          chip.employeesName = sanitizeBlockedValue(String(flatChanges[nameField]));
        }
      }
    }

    return chip.changesLabel();
  }

  return override?.SmartGetFieldValue?.("RefID") ?? appstr("Blank");
};

const BuildFormattingChangeList = changeHandlers => {
  let options = [];
  let doc = StateLib.GetDoc();
  if (!doc) return options;
  let layoutMgr = doc.GetLayoutMgr();
  let overrides = layoutMgr.fOverrideList;
  let numOverrides = overrides.length;
  let sortedOverrides = overrides.sort((a, b) => {
    return SortCompareDates(a.timestamp, b.timestamp);
  });

  for (let overrideIdx = 0; overrideIdx < numOverrides; overrideIdx++) {
    const theOverride = sortedOverrides[overrideIdx];
    const recordId = theOverride.objID;
    const isPosition = theOverride.isPosition ?? false;
    const showBoxOverride = layoutMgr.ShowBoxFormatChange(theOverride);
    const showCellOverride = layoutMgr.ShowCellFormatChange(theOverride);
    if (!showBoxOverride && !showCellOverride) continue;

    const objDB = doc.FindDataObjByRecordID(
      recordId,
      isPosition ? enumObjClassNames.position : enumObjClassNames.box
    );
    if (!objDB) continue;

    let overrideOption = {};
    overrideOption.name = (
      <ActionListItem
        label={GetCCChangeAccordionLabel(BoxChip.FromObjDBObject(objDB).changesLabel(), isPosition)}
        labelProps={{ size: "small", modifier: "bold" }}
        actions={BuildChartDataOverrideActions({
          override: theOverride,
          changeHandlers,
          prefix: "format-change",
          uniqueId: recordId,
          objDB
        })}
      />
    );
    overrideOption.dataTestId = "chart-accordion-format-change-" + recordId;
    overrideOption.timestamp = GetFormattedDateString(new Date(theOverride.timestamp));
    overrideOption.author = theOverride.user;
    overrideOption.jsx = BuildFormattingOverrideContent(theOverride);
    overrideOption.value = { override: theOverride };
    overrideOption.divider = true;
    options.push(overrideOption);
  }
  return options;
};
